import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, ClothingItem, Conversation } from '../types';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { AppHeader, Button, Input, ScreenLayout, Select, PageTitle } from '../components';
import { useResponsive } from '../utils/responsive';
import { Colors, Fonts, Radii, Spacing, FontSizes, Typography } from '../constants';
import { itemsService } from '../services/items';
import { imageService } from '../services/images';
import { conversationsService } from '../services/conversations';
import { salesService, saleErrorKey } from '../services/sales';
import { useDialog, useUser } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'MarkAsSold'>;

/** Sentinel value for the "someone else" (outside the platform) option */
const OTHER_BUYER = '__other__';

/**
 * Parse the price field. Dutch sellers type "12,50" as readily as "12.50", and
 * an empty field is a valid answer meaning "rather not say".
 * @returns the amount, null when left blank, or undefined when unparseable
 */
const parsePrice = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

interface BuyerOption {
  label: string;
  value: string;
}

export const MarkAsSoldScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { user } = useUser();
  const { itemId } = route.params;
  const { isDesktop } = useResponsive();

  const [item, setItem] = useState<ClothingItem | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentUserId = user?.id ?? null;

  const loadData = useCallback(async () => {
    try {
      const [itemData, conversationData] = await Promise.all([
        itemsService.getItemById(itemId),
        conversationsService.getConversationsForItemAsSeller(itemId),
      ]);

      if (!itemData) {
        dialog.alert(t('common.errors.title'), t('screens.markAsSold.loadFailed'), 'error');
        navigation.goBack();
        return;
      }

      if (currentUserId !== itemData.seller_id) {
        dialog.alert(t('common.errors.title'), t('screens.markAsSold.notAllowed'), 'warning');
        navigation.goBack();
        return;
      }

      setItem(itemData);
      setConversations(conversationData);
      // Prefill with the asking price — the common case is that it sold for it.
      setPrice(String(itemData.price).replace('.', ','));
    } catch (error) {
      dialog.alert(t('common.errors.title'), t('screens.markAsSold.loadFailed'), 'error');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  }, [itemId, currentUserId, dialog, t, navigation]);

  useEffect(() => {
    loadData();
  }, [itemId]);

  const buyerOptions: BuyerOption[] = [
    ...conversations
      .filter((conversation) => conversation.buyer)
      .map((conversation) => ({
        label: `${conversation.buyer?.first_name ?? ''} ${conversation.buyer?.last_name ?? ''}`.trim(),
        value: conversation.buyer_id,
      })),
    { label: t('screens.markAsSold.someoneElse'), value: OTHER_BUYER },
  ];

  const isOtherBuyer = selectedBuyer === OTHER_BUYER;

  const handleConfirm = async () => {
    if (!item) return;

    if (!selectedBuyer) {
      dialog.alert(t('common.errors.title'), t('screens.markAsSold.selectBuyerError'), 'warning');
      return;
    }

    const parsedPrice = parsePrice(price);
    if (parsedPrice === undefined) {
      dialog.alert(t('common.errors.title'), t('screens.markAsSold.invalidPrice'), 'warning');
      return;
    }

    const confirmed = await dialog.confirm(
      t('screens.markAsSold.confirmTitle'),
      t('screens.markAsSold.confirmMessage'),
      { variant: 'warning' }
    );

    if (!confirmed) return;

    setSubmitting(true);
    try {
      await salesService.markItemAsSold({
        itemId: item.id,
        buyerId: isOtherBuyer ? undefined : selectedBuyer,
        buyerName: isOtherBuyer ? buyerName : undefined,
        price: parsedPrice,
      });

      dialog.alert(t('common.success'), t('screens.markAsSold.success'), 'success');
      navigation.goBack();
    } catch (error: any) {
      dialog.alert(
        t('common.errors.title'),
        t(`screens.markAsSold.${saleErrorKey(error?.message)}`),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenLayout maxWidth={600} desktopPadding>
      <View style={styles.container}>
        {!isDesktop && (
          <AppHeader
            navigation={navigation}
            currentScreen="ItemDetails"
            title={t('screens.markAsSold.title')}
            showBackButton={true}
          />
        )}
        {loading ? (
          <View style={styles.centerContainer}>
            <Text>{t('common.loading')}</Text>
          </View>
        ) : item ? (
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContentContainer}
          >
            <View style={styles.contentContainer}>
              <View style={styles.content}>
                {isDesktop ? (
                  <PageTitle
                    title={t('screens.markAsSold.title')}
                    subtitle={t('screens.markAsSold.intro', { itemTitle: item.title })}
                    onBack={() =>
                      navigation.canGoBack()
                        ? navigation.goBack()
                        : navigation.navigate('ItemDetails', { itemId })
                    }
                    style={styles.pageTitle}
                  />
                ) : (
                  <Text style={styles.intro}>
                    {t('screens.markAsSold.intro', { itemTitle: item.title })}
                  </Text>
                )}

                {/* Linen summary card: which listing is being marked as sold */}
                <View style={styles.summaryCard}>
                  <View style={styles.summaryThumb}>
                    {item.image_urls?.[0] ? (
                      <Image
                        source={{ uri: imageService.getSmallThumbnailUrl(item.image_urls[0]) }}
                        style={styles.summaryThumbImage}
                        contentFit="cover"
                      />
                    ) : (
                      <Ionicons name="image-outline" size={20} color={Colors.border} />
                    )}
                  </View>
                  <View style={styles.summaryInfo}>
                    <Text style={styles.summaryTitle} numberOfLines={1}>{item.title}</Text>
                    <Text style={styles.summaryMeta} numberOfLines={1}>
                      {t('screens.markAsSold.askingPrice', { price: `€${item.price.toFixed(2)}` })}
                      {item.club?.name ? ` · ${item.club.name}` : ''}
                    </Text>
                  </View>
                </View>

                {conversations.length === 0 && (
                  <Text style={styles.noConversations}>
                    {t('screens.markAsSold.noConversations')}
                  </Text>
                )}

                <Select
                  label={t('screens.markAsSold.buyerLabel')}
                  value={selectedBuyer}
                  options={buyerOptions}
                  onValueChange={setSelectedBuyer}
                  placeholder={t('screens.markAsSold.buyerPlaceholder')}
                />

                {isOtherBuyer && (
                  <Input
                    label={t('screens.markAsSold.buyerNameLabel')}
                    value={buyerName}
                    onChangeText={setBuyerName}
                    placeholder={t('screens.markAsSold.buyerNamePlaceholder')}
                  />
                )}

                <Input
                  label={t('screens.markAsSold.priceLabel')}
                  value={price}
                  onChangeText={setPrice}
                  placeholder={t('screens.markAsSold.pricePlaceholder')}
                  keyboardType="decimal-pad"
                />
                <Text style={styles.priceHint}>
                  {t('screens.markAsSold.priceHint')}
                </Text>

                <Button
                  title={t('screens.markAsSold.confirmButton')}
                  icon="checkmark-circle-outline"
                  onPress={handleConfirm}
                  loading={submitting}
                  variant="primary"
                  style={styles.confirmButton}
                />
                <Button
                  title={t('common.buttons.cancel')}
                  onPress={() =>
                    navigation.canGoBack()
                      ? navigation.goBack()
                      : navigation.navigate('ItemDetails', { itemId })
                  }
                  variant="ghost"
                  disabled={submitting}
                />
              </View>
            </View>
          </ScrollView>
        ) : null}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
  contentContainer: {
    width: '100%',
  },
  content: {
    padding: Spacing.base,
    gap: Spacing.base,
    ...(Platform.OS === 'web' && { paddingHorizontal: 0, paddingTop: 0 }),
  },
  pageTitle: {
    marginBottom: Spacing.sm,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  intro: {
    flex: 1,
    ...Typography.sectionMobile,
    color: Colors.textDark,
  },
  summaryCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.card,
    marginBottom: Spacing.xs,
  },
  summaryThumb: {
    width: 56,
    height: 56,
    borderRadius: Radii.thumb,
    overflow: 'hidden',
    backgroundColor: Colors.card,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  summaryThumbImage: {
    width: '100%',
    height: '100%',
  },
  summaryInfo: {
    flex: 1,
    minWidth: 0,
  },
  summaryTitle: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    lineHeight: 20,
    color: Colors.textDark,
  },
  summaryMeta: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textMedium,
    marginTop: 2,
  },
  noConversations: {
    fontSize: FontSizes.sm,
    color: Colors.textMedium,
  },
  priceHint: {
    fontSize: FontSizes.sm,
    color: Colors.textLight,
    marginTop: -Spacing.xs,
    marginBottom: Spacing.base,
  },
  confirmButton: {
    marginTop: Spacing.sm,
  },
});
